import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import { Observable } from "rxjs";
import { CoreApiService } from "src/app/core/core-api.service";
import { endpoints } from "src/environments/endpoints";
import { environment } from "src/environments/environment";

import { showError } from "../utils/functions/notification.function";
import { encryptStorage } from "../utils/storage/storage";
import { CodigoTransacaoService } from "./codigo-transacao.service";
import Swal from "sweetalert2";

@Injectable({
  providedIn: "root",
})
export class CognitoService {
  private userPool: CognitoUserPool;
  private user: CognitoUser;

  constructor(
    private router: Router,
    private codigo: CodigoTransacaoService,
    private coreApiService: CoreApiService
  ) {
    this.userPool = new CognitoUserPool(environment.cognito);
  }

  signIn(Username: string, Password: string) {
    return new Observable<string>((observer) => {
      const authenticationDetails = new AuthenticationDetails({
        Username,
        Password,
      });

      const user = new CognitoUser({ Username, Pool: this.userPool });

      user.authenticateUser(authenticationDetails, {
        onSuccess: (res) => {
          const idToken = res.getIdToken();
          const email = idToken.payload.email
            ? idToken.payload.email
            : Username;
            const username =
            idToken.payload["cognito:username"] ||
            idToken.payload.family_name ||
            idToken.payload.name;
          encryptStorage.clear();

          encryptStorage.setItem("@user", username, false);
          encryptStorage.setItem("@email", email, false);
          encryptStorage.setItem(
            "@emailVerified",
            idToken.payload.email_verified
          );
          encryptStorage.setItem("@documentoIdentificador", Username, false);
          encryptStorage.setItem("@tipoUsuario", "LIQUIDANTE", false);
          encryptStorage.setItem(
            "@authorization",
            idToken.getJwtToken(),
            false
          );

          user.setSignInUserSession(res);

          this.coreApiService
            .get(
              `${
                endpoints.ibmPermissoes
              }?email=${email}&codigoTransacao=${this.codigo.getUniqueId(5)}`
            )
            .subscribe(
              (r) => {
                if (Boolean(r.usuarioAtivo)) {
                  encryptStorage.setItem("@rbac", atob(r.rbac), true);
                  observer.next("home");
                } else {
                  encryptStorage.clear();
                  observer.error("Usuário Inativo!");
                }
              },
              (e) => {
                this.logout();
                observer.error("Usuário sem permissão de acesso");
              }
            );
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
            delete userAttributes.email_verified;
            delete userAttributes.phone_number_verified;
            delete userAttributes.email;

            Swal.fire({
              title: 'Para garantir a segurança das suas informações, é necessário alterar sua senha no primeiro acesso ao sistema.',
              html: `
                <input type="password" id="novaSenha" class="swal2-input" placeholder="Nova senha">
                <input type="password" id="confirmarSenha" class="swal2-input" placeholder="Confirmar nova senha">
              `,
              focusConfirm: false,
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: 'Confirmar',
              cancelButtonText: 'Cancelar',
              preConfirm: () => {
                const result = {
                  novaSenha: null,
                  result: false
                }
                const novaSenha = (document.getElementById('novaSenha') as HTMLInputElement).value;
                const confirmarSenha = (document.getElementById('confirmarSenha') as HTMLInputElement).value;

                if (!novaSenha || !confirmarSenha) {
                  Swal.showValidationMessage('Preencha todos os campos');
                  return result;
                }
                if (novaSenha !== confirmarSenha) {
                  Swal.showValidationMessage('As senhas não coincidem');
                  return result;
                }

                const senhaValida = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(novaSenha);
                if (!senhaValida) {
                  Swal.showValidationMessage(
                    'A senha deve ter no mínimo 8 caracteres, incluindo pelo menos uma letra maiúscula, uma letra minúscula, um número e um caractere especial.'
                  );
                  return result;
                }

                result.novaSenha = novaSenha;
                result.result = true;
                return result;
              }
            }).then((result) => {
              if (result.isConfirmed) {
                const newPassword = result.value.novaSenha;

                user.completeNewPasswordChallenge(newPassword, userAttributes, {
                    onSuccess: (session) => {
                      Swal.fire('Sucesso!', 'Senha alterada com sucesso.', 'success').then(() => {

                        const idToken = session.getIdToken();
                        const email = idToken.payload.email
                          ? idToken.payload.email
                          : Username;
                          const username =
                          idToken.payload["cognito:username"] ||
                          idToken.payload.family_name ||
                          idToken.payload.name;

                        encryptStorage.clear();
                        encryptStorage.setItem("@user", username, false);
                        encryptStorage.setItem("@email", email, false);
                        encryptStorage.setItem(
                          "@emailVerified",
                          idToken.payload.email_verified
                        );
                        encryptStorage.setItem("@documentoIdentificador", Username, false);
                        encryptStorage.setItem("@tipoUsuario", "LIQUIDANTE", false);
                        encryptStorage.setItem(
                          "@authorization",
                          idToken.getJwtToken(),
                          false
                        );

                        this.coreApiService.get(
                          `${
                            endpoints.ibmPermissoes
                          }?email=${email}&codigoTransacao=${this.codigo.getUniqueId(5)}`
                        ).subscribe(
                          (r) => {
                            if (Boolean(r.usuarioAtivo)) {
                              encryptStorage.setItem("@rbac", atob(r.rbac), true);
                              observer.next("home");
                            } else {
                              encryptStorage.clear();
                              observer.error("Usuário Inativo!");
                            }
                          },
                          (e) => {
                            this.logout();
                            observer.error("Usuário sem permissão de acesso");
                          }
                        );
                      });
                    },
                    onFailure: (err: any) => {
                      observer.error(this.translateCognitoError(err));
                      return;
                    }
                });
              } else {
                this.router.navigate(["/login"]);
              }
            });
        },
        onFailure: (e: any) => {
          observer.error(this.translateCognitoError(e));
          return;
        },
      });
    });
  }

  signUp(
    Username: string,
    password: string,
    attributes: { [key: string]: string }
  ) {
    const atributeList = Object.keys(attributes).map(
      (key) => new CognitoUserAttribute({ Name: key, Value: attributes[key] })
    );

    return new Observable<ISignUpResult>((observer) => {
      this.userPool.signUp(
        Username,
        password,
        atributeList,
        null,
        (err: Error | null, result: ISignUpResult) => {
          if (err) {
            observer.error(this.translateCognitoError(err));
            return;
          }

          observer.next(result);
          observer.complete();
        }
      );
    });
  }

  confirmAccount(token: string, newUsername?: string) {
    return new Observable((observer) => {
      if (!newUsername) {
        this.user = this.currentUser();
      } else {
        this.user = new CognitoUser({
          Username: newUsername,
          Pool: this.userPool,
        });
      }

      this.user.confirmRegistration(token, true, (e, res) => {
        if (e) {
          observer.error(this.translateCognitoError(e));
          return;
        }

        observer.next(res);
        observer.complete();
      });
    });
  }

  update(attributes: { [key: string]: string }) {
    return new Observable((observer) => {
      if (this.currentUser()) {
        const atributeList = Object.keys(attributes).map((key) => ({
          Name: key,
          Value: attributes[key],
        }));

        this.user.updateAttributes(atributeList, (err, res) => {
          if (err) {
            observer.error(this.translateCognitoError(err));
            return;
          }

          observer.next(res);
          observer.complete();
        });
      }
    });
  }

  // async checkUser(documentoIdentificador: string) {
  //   const myHeaders = new Headers();
  //   myHeaders.append("x-api-key", "VtdbC8CWd58Ps0quKyhtf6zyIVgFiypD23nKsFyl");

  //   return fetch(
  //     `https://acn44riag5.execute-api.us-east-1.amazonaws.com/v1/usuario/${documentoIdentificador}`,
  //     {
  //       method: "DELETE",
  //       redirect: "follow",
  //       headers: myHeaders,
  //     }
  //   )
  //     .then((response) => response.json())
  //     .then((result) => result)
  //     .catch((error) => error);
  // }

  //Password
  changePassword(currentPassword: string, newPassword: string) {
    return new Observable<string>((observer) => {
      if (this.currentUser()) {
        this.user.changePassword(currentPassword, newPassword, (err, res) => {
          if (err) {
            observer.error(this.translateCognitoError(err));
            return;
          }

          observer.next(res);
          observer.complete();
        });
      }
    });
  }

  resetPassword(Username: string) {
    return new Observable<string>((observer) => {
      const user = new CognitoUser({
        Username,
        Pool: this.userPool,
      });

      user.forgotPassword({
        onSuccess: (res) => {
          observer.next(res);
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(this.translateCognitoError(err));
          return;
        },
      });
    });
  }

  confirmPassword(Username: string, newPassword: string, token: string) {
    return new Observable<string>((observer) => {
      const user = new CognitoUser({
        Username,
        Pool: this.userPool,
      });

      user.confirmPassword(token, newPassword, {
        onSuccess: (res) => {
          observer.next(res);
          observer.complete();
        },
        onFailure: (err) => {
          observer.error(this.translateCognitoError(err));
          return;
        },
      });
    });
  }

  //E-mail
  resendEmailCode(newUsername?: string) {
    return new Observable((observer) => {
      if (!newUsername) {
        this.user = this.currentUser();
      } else {
        this.user = new CognitoUser({
          Username: newUsername,
          Pool: this.userPool,
        });
      }

      this.user.resendConfirmationCode((e: Error, res: any) => {
        if (e) {
          observer.error(this.translateCognitoError(e));
          return;
        }

        observer.next(res);
        observer.complete();
      });
    });
  }

  confirmEmail(token: string) {
    return new Observable((observer) => {
      if (this.currentUser()) {
        this.user.verifyAttribute("email", token, {
          onSuccess(res) {
            observer.next(res);
            observer.complete();
          },
          onFailure(err) {
            observer.error(this.translateCognitoError(err));
            return;
          },
        });
      }
    });
  }

  //JwtToken
  refreshToken() {
    return new Observable((observer) => {
      if (this.currentUser()) {
        this.user.getSession((err: any, session: any) => {
          if (err || !session.isValid()) {
            observer.error(err || "Sessão inválida.");
            return;
          }

          this.user.refreshSession(
            session.getRefreshToken(),
            (err, session) => {
              if (err) {
                observer.error(this.translateCognitoError(err));
                return;
              }

              observer.next(session.getIdToken().getJwtToken());
              observer.complete();
            }
          );
        });
      }
    });
  }

  logout() {
    this.user = this.userPool.getCurrentUser();
    if (this.user) {
      this.user.signOut();
      encryptStorage.clear();
      this.router.navigate(["/login"]);
    } else {
      encryptStorage.clear();
      this.router.navigate(["/login"]);
    }
  }

  private currentUser(): CognitoUser | null {
    this.user = this.userPool.getCurrentUser();

    if (!this.user) {
      showError("", "Usuário não autenticado");
      return null;
    }

    this.user.getSession((err, session) => {
      if (err) {
        showError("", "Não foi possivel validar a sessão.");
        return null;
      }
    });

    return this.user;
  }

  private translateCognitoError(error: Error | null): Error {
    if (!!error) {
      const errorMessages: { [key: string]: string } = {
        UserNotFoundException: "Usuário não encontrado.",
        NotAuthorizedException: "Credenciais inválidas.",
        UsernameExistsException:
          "Usuário já cadastrado, faça o login e associe a sua empresa através do menu Meu Perfil.",
        InvalidPasswordException:
          "A senha não atende aos critérios de segurança.",
        CodeMismatchException: "O código de verificação está incorreto.",
        ExpiredCodeException: "O código de verificação expirou.",
        LimitExceededException: "Excedeu o número de tentativas permitidas.",
        PasswordResetRequiredException:
          "O usuário precisa redefinir a senha antes de continuar.",
        UserNotConfirmedException: "O usuário ainda não confirmou o registro.",
        ServiceUnavailableException:
          "O serviço está temporariamente indisponível.",
        InternalErrorException: "Ocorreu um erro interno no serviço.",
        CodeMismatchExpection: "O código de verificação está incorreto",
        UserLambdaValidationException: "E-mail já cadastrado!",
        MinimumLengthField: "A senha deve ter no mínimo 8 caracteres",
        RequireUppercaseField: "A senha deve conter pelo menos uma letra maiúscula",
        RequireLowercaseField: "A senha deve conter pelo menos uma letra minúscula",
        RequireNumbersField: "A senha deve conter pelo menos um número",
        RequireSymbolsField: "A senha deve conter pelo menos um símbolo (ex: !, @, #, $)",
        PasswordHistoryPolicyViolationException: "Escolha uma nova senha diferente das últimas que você usou.",
      };

      if (error.name === "NotAuthorizedException") {
        if (error.message === "User is disabled.") {
          error.message = "Usuário sem permissão de acesso";
        } else {
          error.message = "Credenciais inválidas.";
        }
      } else {
        error.message = errorMessages[error.name] || "Ocorreu um erro desconhecido.";
      }
      return error;
    }

    error.name = "InvalidError";
    error.message = "Ocorreu um erro desconhecido.";

    return error;
  }
}
